import { cellAtWorld, heroWalkable } from '../grid.ts';
import { iatan2, idiv, imax, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Enemy, Hero, SendaNpc } from '../types.ts';
import type { Pt } from '../caza-types.ts';
import type { SwellRules, SwellState } from '../bote-types.ts';
import { damageEnemy, damageHero, ev, spawnEnemy } from './combat.ts';
import { climbAt, crowsAt, gnawHold, gnawTick, gunwaleNear, heldAt, onDeck, onHull } from './bote-boat.ts';

/**
 * The swell (docs/sendas/barbaro.md, chapter 3, act 2: «El oleaje»; the ground in battle/bote-layout.ts). A storm: big
 * waves lift the bow, the water crosses the deck. Every so often a big one comes, the bow rising three seconds before:
 * whatever isn't holding to a shield on the gunwale slides astern, and what was astern goes into the sea (the pack too:
 * the rule of the arches' gusts, along the boat). The people hold on, unless a wolf was at them as it came: then the
 * water takes them, and the hero at the gunwale by them within ten seconds hauls them back. From the boat the bow rope
 * swings the hero over to a floe (a tap on it; Salto or Carga too): the pack climbs onto the floes and leaps aboard from
 * them, unless the hero is there. Won with six big waves passed; lost with two of the boat's people taken, the boat
 * broken (40 blows) or the hero down.
 *
 * Cost: a wave looks at the deck once; the people a short list.
 */

const PEOPLE = ['bera', 'sten', 'rower1', 'rower2', 'rower3', 'rower4'];

const fresh = (r: SwellRules): SwellState => ({
  wave: 0,
  on: -1,
  to: -1,
  from: { x: 0, y: 0 },
  dest: { x: 0, y: 0 },
  t0: 0,
  swept: [],
  hitAt: PEOPLE.map(() => -1),
  lost: 0,
  hauled: 0,
  falls: 0,
  overs: 0,
  waiting: [],
  next: r.climb.first,
  floeNext: r.floe.first,
  crow: r.crows.first,
  gnawCd: r.boat.gnaw,
  done: -1,
  lostAt: -1,
});

const sr = (s: BattleState): SwellRules | undefined => s.rules?.senda?.bote?.swell;
const ss = (s: BattleState): SwellState | undefined => s.senda?.bote?.swell;

/** The floe a cell is of (−1: none). */
export const swellFloeOf = (r: SwellRules, cell: number): number => {
  let lo = 0;
  let hi = r.floeCells.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const c = r.floeCells[mid] as number;
    if (c === cell) return r.floeOf[mid] as number;
    if (c < cell) lo = mid + 1;
    else hi = mid - 1;
  }
  return -1;
};

/** The next big wave's warning is out (the bow rising). */
export const swellWarn = (s: BattleState): boolean => {
  const r = sr(s);
  const w = ss(s);
  const t = r && w ? r.plan[w.wave] : undefined;
  return !!r && t !== undefined && s.waveT >= t - r.warn;
};

/** Ticks to the next big wave (−1: none left). */
export const swellNext = (s: BattleState): number => {
  const r = sr(s);
  const w = ss(s);
  const t = r && w ? r.plan[w.wave] : undefined;
  return t === undefined ? -1 : imax(0, t - s.waveT);
};

/** A tap: 0 the boat (back from a floe), 1 + k the k-th floe (over by the bow rope, from the boat). */
export const swellOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = sr(s);
  const w = ss(s);
  if (!r || !w || w.to !== -1 || h.downTicks > 0) return;
  if (at === 0) {
    if (w.on < 0) return;
    w.to = -2;
    w.dest = gunwaleNear(r.boat, h.x, h.y);
  } else {
    const k = at - 1;
    const f = r.floes[k];
    if (!f || w.on === k || !withinRange(f.x, f.y, h.x, h.y, r.ropeReach)) return;
    w.to = k;
    w.dest = { ...f };
  }
  w.from = { x: h.x, y: h.y };
  w.t0 = s.tick;
  h.path = [];
  h.moving = false;
  h.target = 0;
  h.forced = false;
  ev(s, { t: 'senda', kind: 'bRope', npc: '', x: w.dest.x, y: w.dest.y, n: w.to });
};

const npc = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id && n.hp > 0);

/** Pushed astern along the deck: true if it went over the stern. */
const slide = (s: BattleState, r: SwellRules, o: { x: number; y: number }): boolean => {
  const y = o.y + r.push;
  if (y > r.boat.stern) return true;
  o.y = y;
  // Narrower astern: in toward the keel till it stands on the planks.
  for (let k = 0; k < 4 && !heroWalkable(s, cellAtWorld(s, o.x, o.y)); k++) o.x = idiv(o.x, 2);
  return false;
};

const wave = (s: BattleState, r: SwellRules, w: SwellState, cue: (c: string) => void): void => {
  w.wave++;
  ev(s, { t: 'senda', kind: 'bWave', npc: '', x: 0, y: r.boat.bow, n: w.wave });
  const h = s.heroes.find((x) => x.downTicks === 0);
  for (const e of s.enemies) {
    if (!onDeck(e) || !onHull(r.boat, cellAtWorld(s, e.x, e.y)) || heldAt(r.boat, e.x, e.y, r.hold)) continue;
    if (!slide(s, r, e)) continue;
    ev(s, { t: 'senda', kind: 'bOverboard', npc: '', x: e.x, y: r.boat.stern + 600, n: 0 });
    damageEnemy(s, e, e.hp + e.maxHp, { pid: h?.pid ?? '', ignoresArmor: true });
    w.overs++;
    cue('over1');
  }
  // The people: one struck just now loses the grip, and the water takes it.
  for (let k = 0; k < PEOPLE.length; k++) {
    const id = PEOPLE[k] as string;
    const n = npc(s, id);
    const at = w.hitAt[k] as number;
    if (!n || n.away || at < 0 || s.tick - at > r.shaken || w.swept.some((p) => p.id === id && !p.lost)) continue;
    n.away = true;
    w.swept.push({ id, x: n.x, y: n.y, until: s.tick + r.haulT, lost: false });
    ev(s, { t: 'senda', kind: 'bSwept', npc: id, x: n.x + (n.x >= 0 ? 1600 : -1600), y: n.y + 600 });
    cue(`swept${w.swept.length}`);
  }
  if (!h || w.on >= 0 || w.to !== -1 || heldAt(r.boat, h.x, h.y, r.hold)) return;
  ev(s, { t: 'senda', kind: 'bSlide', npc: '', x: h.x, y: h.y, n: r.push });
  if (!slide(s, r, h)) {
    h.ax = h.x;
    h.ay = h.y;
    h.destX = h.x;
    h.destY = h.y;
    h.path = [];
    h.moving = false;
    return;
  }
  // Over the stern: into the sea, and hauled back over the transom, hurt.
  ev(s, { t: 'senda', kind: 'bOverboard', npc: 'hero', x: h.x, y: r.boat.stern + 600, n: 1 });
  damageHero(s, h, mulBp(h.stats.maxHp, r.fallBp), null, 'physical', true);
  h.x = h.destX = h.ax = 0;
  h.y = h.destY = h.ay = r.boat.stern - 900;
  h.path = [];
  h.moving = false;
  h.stun = imax(h.stun, r.stun);
  w.falls++;
  cue('fall1');
};

export const swellTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = sr(s);
  const st = s.senda;
  if (!r || !st) return;
  const bs = (st.bote ??= {});
  const w = (bs.swell ??= fresh(r));
  if (!s.started || w.done >= 0 || w.lostAt >= 0) return;
  const h = s.heroes.find((x) => x.downTicks === 0);
  // The people's grip: a blow shakes it for `shaken` (the base rule sets a waiting person's flinch and never counts it
  // down: the act reads a fresh blow off it and keeps the time itself).
  PEOPLE.forEach((id, k) => {
    const n = npc(s, id);
    if (!n || n.flinch <= 0) return;
    w.hitAt[k] = s.tick;
    n.flinch = 0;
  });
  // The bow rope: across in a swing (the drawing sags it), then on the floe or back aboard.
  if (h && w.to !== -1) {
    const k = imax(0, s.tick - w.t0);
    h.x = h.destX = h.ax = w.from.x + idiv((w.dest.x - w.from.x) * k, r.rope);
    h.y = h.destY = h.ay = w.from.y + idiv((w.dest.y - w.from.y) * k, r.rope);
    h.path = [];
    h.moving = false;
    if (k >= r.rope) {
      w.on = w.to === -2 ? -1 : w.to;
      w.to = -1;
      ev(s, { t: 'senda', kind: 'bLand', npc: '', x: h.x, y: h.y, n: w.on });
      if (w.on >= 0) cue('floe1');
    }
  } else if (h) {
    // (A leap or a charge over the water lands it wherever it lands.)
    const c = cellAtWorld(s, h.x, h.y);
    w.on = onHull(r.boat, c) ? -1 : swellFloeOf(r, c) >= 0 ? swellFloeOf(r, c) : w.on;
  }
  // The big waves.
  const t = r.plan[w.wave];
  if (t !== undefined) {
    if (s.waveT === t - r.warn) {
      ev(s, { t: 'senda', kind: 'bWaveWarn', npc: '', x: 0, y: r.boat.bow, n: r.warn });
      cue(`warn${w.wave + 1}`);
    }
    if (s.waveT >= t) wave(s, r, w, cue);
  }
  if (w.wave >= r.need) {
    w.done = s.tick;
    cue('calm');
    return;
  }
  // The swept: hauled back by the hero at the gunwale by them, or lost.
  for (const p of w.swept) {
    if (p.lost || p.until < 0) continue;
    const n = s.senda?.npcs.find((x) => x.id === p.id);
    if (h && w.on < 0 && w.to === -1 && withinRange(h.x, h.y, p.x, p.y, r.haul)) {
      p.until = -1;
      w.hauled++;
      if (n) n.away = false;
      ev(s, { t: 'senda', kind: 'bHaul', npc: p.id, x: p.x, y: p.y });
      cue('haul');
    } else if (s.tick >= p.until) {
      p.lost = true;
      w.lost++;
      ev(s, { t: 'senda', kind: 'bLost', npc: p.id, x: p.x, y: p.y, n: w.lost });
      cue(`lost${w.lost}`);
      if (w.lost >= r.lostMax) w.lostAt = s.tick;
    }
  }
  // Wolves at the oars; bands onto the floes (they leap aboard from there unless the hero is on that floe).
  if (s.waveT >= w.next) {
    w.next = s.waveT + rngRange(s.rng, r.climb.every[0], r.climb.every[1]);
    const n = rngRange(s.rng, r.climb.n[0], r.climb.n[1]);
    for (let i = 0; i < n; i++) climbAt(s, r.boat, rngInt(s.rng, r.boat.oars.length), r.wolfHp);
  }
  if (s.waveT >= w.floeNext) {
    w.floeNext = s.waveT + rngRange(s.rng, r.floe.every[0], r.floe.every[1]);
    const k = rngInt(s.rng, r.floes.length);
    const f = r.floes[k] as Pt;
    const n = rngRange(s.rng, r.floe.n[0], r.floe.n[1]);
    for (let i = 0; i < n; i++) {
      const e = spawnEnemy(s, 'wolf', 1, false, 0, f.x + ((i % 3) - 1) * 450, f.y + (i >> 1) * 400 - 200, r.wolfHp);
      w.waiting.push([e.id, k, s.tick + r.floe.gather]);
    }
  }
  let j = 0;
  for (const q of w.waiting) {
    const e = s.enemies.find((x) => x.id === q[0]);
    if (!e || e.hp <= 0) continue;
    if (s.tick >= q[2] && w.on !== q[1]) {
      // Over the water onto the gunwale nearest.
      const p = gunwaleNear(r.boat, e.x, e.y);
      e.x = p.x + ((e.id % 3) - 1) * 250;
      e.y = p.y + ((e.id % 5) - 2) * 200;
      ev(s, { t: 'senda', kind: 'bFloeLeap', npc: '', x: e.x, y: e.y, n: q[1] });
      continue;
    }
    w.waiting[j++] = q;
  }
  w.waiting.length = j;
  if (h && s.waveT >= w.crow) {
    w.crow = s.waveT + r.crows.every;
    crowsAt(s, r.crows.n, h.x - 7000, h.y - 3000, r.wolfHp);
  }
  w.gnawCd = gnawTick(s, r.boat, w.gnawCd);
};

/** The wolves waiting on a floe prowl there (unless the hero is on it); the gnawers at the hull (true: the act moved it). */
export const swellHold = (s: BattleState, e: Enemy): boolean => {
  const r = sr(s);
  const w = ss(s);
  if (!r || !w || e.stun > 0) return false;
  const q = w.waiting.find((x) => x[0] === e.id);
  if (q && w.on !== q[1]) {
    // Facing the boat across the water, waiting to leap.
    if (e.x !== 0 || e.y !== 0) e.facing = iatan2(-e.y, -e.x);
    e.anim = 'idle';
    return true;
  }
  return gnawHold(s, r.boat, e);
};

/** The big waves passed, of those to pass. */
export const swellGoal = (s: BattleState): { done: number; need: number } => {
  const r = sr(s);
  const w = ss(s);
  if (!r || !w) return { done: 0, need: 1 };
  return { done: w.wave, need: r.need };
};

export const swellResult = (s: BattleState): 'won' | 'lost' | null => {
  const w = ss(s);
  if (!w) return null;
  if (w.lostAt >= 0 && s.tick >= w.lostAt + 24) return 'lost';
  return w.done >= 0 && s.tick >= w.done + 24 ? 'won' : null;
};
